import React, { useEffect, useMemo, useState } from 'react';
import { X, Save, Plus, Trash2, ChevronUp, ChevronDown, ImageIcon, Eye, MapPin, Building2, Square, Check, Tag, Phone, MessageCircle, Facebook, Loader2, Copy } from 'lucide-react';
import type { Property, Agent, MediaFile } from '../../types/cms';
import {
  districts,
  categories,
  listingTypes,
} from '../../utils/propertyFilters';
import { districtLabels, categoryLabels } from '../../utils/helpers';
import { MediaPicker } from './MediaPicker';
import { PropertyStatusBadge } from '../common/PropertyStatusBadge';

interface PropertyEditorProps {
  property: Property;
  agents: Agent[];
  media: MediaFile[];
  apiBase: string;
  token: string;
  onSave: (property: Property) => void;
  onCancel: () => void;
  onUploadMedia?: (file: File, meta?: { category?: string; tags?: string[] }) => Promise<string | undefined | void>;
  onDeleteMedia?: (id: string) => Promise<void>;
  usedMediaIds?: Set<string>;
  onRefreshMedia?: () => void;
  mode?: 'create' | 'edit' | 'approve';
  isAdmin?: boolean;
  staffUsers?: { id: string; username: string; role: string; agentId?: string }[];
}

// Facebook 推廣 hashtag 標籤庫 fallback（CMS siteConfig.socialPromotion.hashtagGroups 缺漏時使用）
const FALLBACK_HASHTAG_GROUPS: { name: string; tags: string[] }[] = [
  { name: '地區', tags: ['#荃灣', '#葵芳', '#葵興', '#荔枝角', '#地鐵'] },
  { name: '類型', tags: ['#工廈', '#商廈', '#工商廈', '#地產', '#辦公室', '#寫字樓', '#倉', '#食物工場', '#24工作室'] },
  { name: '租售', tags: ['#售', '#租', '#交吉'] },
  { name: '特色', tags: ['#荀盤', '#四正無柱', '#間隔實用', '#高樓底', '#新裝修', '#內廁', '#有貨台', '#原裝', '#冷凍倉', '#大雪櫃', '#可入櫃', '#半倉', '#適合各行各業', '#創業'] },
];

// 標籤正規化：全形＃→#、補 # 前綴、去空白、去重
const normalizeTag = (raw: string): string => {
  const t = raw.trim().replace(/^＃+/, '').replace(/^#+/, '').replace(/\s+/g, '');
  return t ? `#${t}` : '';
};

const TextInput = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder = '',
}: {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) => (
  <div className="mb-4">
    <label className="mb-1 block text-sm font-semibold text-slate-700">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none"
    />
  </div>
);

const TextArea = ({
  label,
  value,
  onChange,
  rows = 4,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) => (
  <div className="mb-4">
    <label className="mb-1 block text-sm font-semibold text-slate-700">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none"
    />
  </div>
);

const Select = ({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) => (
  <div className="mb-4">
    <label className="mb-1 block text-sm font-semibold text-slate-700">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm focus:border-gold-600 focus:outline-none"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

export const PropertyEditor: React.FC<PropertyEditorProps> = ({
  property: initialProperty,
  agents,
  media,
  apiBase,
  token,
  onSave,
  onCancel,
  onUploadMedia,
  onDeleteMedia,
  usedMediaIds,
  onRefreshMedia,
  mode = 'edit',
  isAdmin,
  staffUsers,
}) => {
  const [property, setProperty] = useState<Property>({
    ...initialProperty,
    status: initialProperty.status || 'available',
  });
  const [tagsText, setTagsText] = useState(initialProperty.tags.join(', '));
  const [featuresText, setFeaturesText] = useState(initialProperty.features.join(', '));
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showFloorPlanPicker, setShowFloorPlanPicker] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<{ idx: number; url: string } | null>(null);
  // 相簿多選批次刪除（2026-08-27 用戶指示：逐一刪除確認太不便）
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Set<number>>(new Set());
  const [batchPurging, setBatchPurging] = useState(false);
  // 相簿拖曳排序（2026-08-28 用戶指示：桌面拖到想要的位置，取代逐步移位；手機保留箭頭）
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  // ── 社群推廣（FB 照片發佈——production 入口，2026-08-23）──
  const [socialOpen, setSocialOpen] = useState(false);
  const [socialTab, setSocialTab] = useState<'photo' | 'video'>('photo');
  // 影片 tab 由 KV staging_features 控制（模組化開關，預設關閉；Docs/PROMPT_MAINTAIN.md §5.0）
  const [videoPublishEnabled, setVideoPublishEnabled] = useState(false);
  const [selectedImageUrls, setSelectedImageUrls] = useState<string[]>([]);
  const [customMessage, setCustomMessage] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [confirmPublic, setConfirmPublic] = useState(false);
  const [shortLink, setShortLink] = useState('');
  const [analytics, setAnalytics] = useState<{ views: number; fbClicks: number } | null>(null);
  // Facebook 推廣 hashtag：分組標籤牆（CMS 載入）＋已選標籤＋自由輸入
  const [hashtagGroups, setHashtagGroups] = useState<{ name: string; tags: string[] }[]>(FALLBACK_HASHTAG_GROUPS);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [customTagInput, setCustomTagInput] = useState('');
  const [msgCopied, setMsgCopied] = useState(false);

  useEffect(() => {
    fetch(`${apiBase}/api/staging/features`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && typeof d.videoPublish === 'boolean') setVideoPublishEnabled(d.videoPublish);
      })
      .catch(() => {/* 取不到 flag 維持關閉 */});
  }, [apiBase]);

  // 展開時生成短連結（帶 utm 追蹤）；失敗 fallback 完整 URL
  useEffect(() => {
    if (!socialOpen || shortLink) return;
    fetch(`${apiBase}/api/staging/shortlink-test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url: `https://marveland.hk/properties/${property.id}?utm_source=facebook&utm_medium=photo&utm_campaign=${property.id}`,
      }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.success && d.shortLink) setShortLink(d.shortLink);
      })
      .catch(() => {/* fallback 完整 URL */});
    // 載入此物業的流量成效（FB 短鏈點擊＋前台瀏覽）
    fetch(`${apiBase}/api/analytics/property/${property.id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && typeof d.views === 'number') setAnalytics({ views: d.views, fbClicks: d.fbClicks || 0 });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialOpen]);

  useEffect(() => {
    if (socialOpen && selectedImageUrls.length === 0 && property.images.length > 0) {
      setSelectedImageUrls(property.images.slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialOpen]);

  // 相簿變動（刪除/新增）時同步 FB 選圖：剔除已移除的 URL、自動補入新照片（上限 10 張）
  // 修復：相簿刪相後 FB 區仍拿著死引用 → 發佈報「Missing or invalid image file」
  useEffect(() => {
    setSelectedImageUrls((prev) => {
      const valid = prev.filter((u) => property.images.includes(u));
      const added = property.images.filter((u) => !valid.includes(u));
      const next = [...valid, ...added].slice(0, 10);
      if (next.length === prev.length && next.every((u, i) => u === prev[i])) return prev;
      return next;
    });
  }, [property.images]);

  // 展開時載入 CMS 標籤庫＋依物業資料智慧預選（地區/類型/租售），只預選一次不覆蓋客人手動選擇
  useEffect(() => {
    if (!socialOpen || selectedTags.length > 0) return;
    fetch(`${apiBase}/api/cms/data`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const groups = d?.siteConfig?.socialPromotion?.hashtagGroups;
        if (Array.isArray(groups) && groups.length > 0) {
          setHashtagGroups(groups);
        }
        const allTags: string[] = (groups && groups.length > 0 ? groups : FALLBACK_HASHTAG_GROUPS).flatMap((g: { tags: string[] }) => g.tags);
        const districtMap: Record<string, string[]> = {
          tsuen_wan: ['#荃灣'], kwai_chung: ['#葵芳', '#葵興'], lai_chi_kok: ['#荔枝角'],
        };
        const categoryMap: Record<string, string[]> = {
          office: ['#寫字樓', '#辦公室', '#商廈'], industrial: ['#工廈', '#工商廈'], shop: ['#商廈'],
        };
        const listingMap: Record<string, string[]> = {
          rent: ['#租'], sale: ['#售'], sale_or_rent: ['#售', '#租'],
        };
        const suggested = [
          ...(districtMap[initialProperty.district] || []),
          ...(categoryMap[initialProperty.category] || []),
          ...(listingMap[initialProperty.listingType] || []),
        ];
        setSelectedTags(suggested.filter((t) => allTags.includes(t)));
      })
      .catch(() => {/* 載入失敗用 fallback 標籤庫 */});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialOpen]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const addCustomTag = () => {
    const tag = normalizeTag(customTagInput);
    if (!tag) return;
    setSelectedTags((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
    setCustomTagInput('');
  };

  // 2026-08-23 用戶指示：貼文不含代理姓名/WhatsApp（物業頁上已有，貼文點短連結即見）
  // 價格規則對齊前台 PropertyDetail：rent 且有 rentPrice 用租金；sale_or_rent 雙價並列（修復「$0HKD」＝誤取 price 欄）
  const rentLabel = `租 $${(property.rentPrice || 0).toLocaleString()}${property.rentPriceUnit || ' / 月'}`;
  const saleLabel = `售 $${property.price.toLocaleString()}${property.priceUnit || 'HKD'}`;
  const priceLabel =
    property.listingType === 'rent'
      ? property.rentPrice
        ? rentLabel
        : `租 $${property.price.toLocaleString()}${property.priceUnit || 'HKD'}`
      : property.listingType === 'sale_or_rent'
      ? `${saleLabel} / ${rentLabel}`
      : saleLabel;
  const propertyLink = shortLink || `https://marveland.hk/properties/${property.id}`;
  // 品牌標籤固定＋所選標籤（標籤牆）串成一行，附加在文案尾
  const tagLine = ['#Marveland', '#金晉物業', ...selectedTags].join(' ');
  const autoMessage =
    `【新盤速遞】${property.title}\n${priceLabel} | 面積：約 ${property.areaGross} 呎\n地址：${property.address}\n\n` +
    `${property.description}\n\n立即了解：${propertyLink}`;
  const fullMessage = customMessage.trim()
    ? `${customMessage.trim()}\n\n${tagLine}`
    : `${autoMessage}\n\n${tagLine}`;

  // 直接公開發佈（2026-08-23 用戶指示：不設測試模式——不滿意就到 FB 刪貼文重發）
  const doPublish = async () => {
    setPublishing(true);
    setPublishResult(null);
    try {
      const res = await fetch(`${apiBase}/api/admin/fb-publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          propertyId: property.id,
          message: fullMessage,
          imageUrls: selectedImageUrls,
          testMode: false,
        }),
      });
      const data = await res.json();
      setPublishResult({ ok: !!data.success, message: data.message || data.error || `HTTP ${res.status}` });
      if (data.success) {
        setProperty((p) => ({ ...p, fbPublishedAt: new Date().toISOString() }));
      }
    } catch (e) {
      setPublishResult({ ok: false, message: e instanceof Error ? e.message : String(e) });
    }
    setPublishing(false);
  };

  const toggleImage = (url: string) => {
    setSelectedImageUrls((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : prev.length < 10 ? [...prev, url] : prev
    );
  };

  // 複製最終文案（自動生成或覆寫版）——方便客人 copy & edit
  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(fullMessage);
      setMsgCopied(true);
      setTimeout(() => setMsgCopied(false), 2000);
    } catch {
      window.prompt('複製以下文案：', fullMessage);
    }
  };

  const agentOptions = useMemo(
    () => [
      { value: '', label: '未分配' },
      ...agents.map((a) => ({ value: a.id, label: `${a.name} · ${a.title}` })),
    ],
    [agents]
  );
  const previewAgent = agents.find((a) => a.id === property.agentId);

  const linkedUserForAgent = (agentId: string) =>
    staffUsers?.find((u) => u.agentId === agentId);

  // 負責人 = 後台權限擁有者：當負責人或 staffUsers 變化時，自動同步 createdBy
  useEffect(() => {
    if (!staffUsers || staffUsers.length === 0) return;
    const linked = linkedUserForAgent(property.agentId);
    const expected = linked ? linked.username.trim().toLowerCase() : undefined;
    const current = (property.createdBy || '').trim().toLowerCase() || undefined;
    if (expected !== current) {
      update('createdBy', expected);
      console.log('[PropertyEditor] auto-mapped createdBy:', expected);
    }
  }, [property.agentId, staffUsers]);

  const update = <K extends keyof Property>(key: K, value: Property[K]) => {
    setProperty((p) => ({ ...p, [key]: value }));
  };

  const handleNumber = (key: keyof Property, value: string) => {
    const num = value === '' ? 0 : Number(value);
    update(key, num as Property[keyof Property]);
  };

  const handleTagsChange = (v: string) => {
    setTagsText(v);
    update(
      'tags',
      v
        .split(/[,，]/)
        .map((t) => t.trim())
        .filter(Boolean)
    );
  };

  const handleFeaturesChange = (v: string) => {
    setFeaturesText(v);
    update(
      'features',
      v
        .split(/[,，]/)
        .map((t) => t.trim())
        .filter(Boolean)
    );
  };

  const addImages = (urls: string[]) => {
    // 去重：相同 URL（含既有的）只保留一張，避免「加幾張變一大堆」
    update('images', [...new Set([...property.images, ...urls])]);
  };

  const removeImage = (idx: number) => {
    update(
      'images',
      property.images.filter((_, i) => i !== idx)
    );
  };

  const extractMediaId = (url: string): string | null => {
    const prefix = `${apiBase}/api/media/`;
    if (url.startsWith(prefix)) {
      return url.slice(prefix.length);
    }
    return null;
  };

  const deleteMediaFromLibrary = async (mediaId: string) => {
    try {
      const res = await fetch(`${apiBase}/api/media/${mediaId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`刪除媒體失敗 (${res.status}): ${body || res.statusText}`);
      }
    } catch (err) {
      window.alert(err instanceof Error ? err.message : '刪除媒體失敗');
      throw err;
    }
  };

  const handleRemoveImageOnly = () => {
    if (!imageToDelete) return;
    removeImage(imageToDelete.idx);
    setImageToDelete(null);
  };

  // ===== 相簿多選批次刪除 =====
  const toggleSelectImage = (idx: number) => {
    setSelectedForDelete((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const exitMultiSelect = () => {
    setMultiSelectMode(false);
    setSelectedForDelete(new Set());
  };

  const removeSelected = (indexes: Set<number>) => {
    update('images', property.images.filter((_, i) => !indexes.has(i)));
    exitMultiSelect();
  };

  const handleBatchRemoveOnly = () => {
    if (selectedForDelete.size === 0) return;
    removeSelected(selectedForDelete);
  };

  const handleBatchRemoveAndDeleteMedia = async () => {
    if (selectedForDelete.size === 0 || batchPurging) return;
    const ids = [...selectedForDelete]
      .map((i) => extractMediaId(property.images[i]))
      .filter((x): x is string => !!x);
    setBatchPurging(true);
    try {
      if (ids.length === 0) {
        window.alert('所選圖片無法識別媒體 ID，僅從本物業移除');
      } else {
        // 伺服器以最新資料核實：其他物業仍在用的照片自動保留
        const res = await fetch(`${apiBase}/api/media/purge-if-unused`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids, propertyId: property.id }),
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.success) {
          window.alert(`批次刪除失敗：${data?.error || `HTTP ${res.status}`}（已改為僅從本物業移除）`);
        } else {
          const purgedCount = data.purged?.length || 0;
          const keptCount = Object.keys(data.keptInUse || {}).length;
          window.alert(`批次完成：${purgedCount} 張已從媒體庫連根拔除；${keptCount} 張仍被其他物業使用，僅從本物業移除。`);
        }
      }
      removeSelected(selectedForDelete);
    } catch (err) {
      window.alert(`刪除請求失敗：${err instanceof Error ? err.message : String(err)}（已改為僅從本物業移除）`);
      removeSelected(selectedForDelete);
    } finally {
      setBatchPurging(false);
    }
  };

  const handleRemoveImageAndDeleteMedia = async () => {
    if (!imageToDelete) return;
    const mediaId = extractMediaId(imageToDelete.url);
    if (!mediaId) {
      window.alert('無法識別媒體 ID，僅從本物業移除');
      removeImage(imageToDelete.idx);
      setImageToDelete(null);
      return;
    }
    // 2026-08-27 用戶定案：連根拔起前由伺服器以最新資料核實——其他物業仍在用的照片自動保留
    try {
      const res = await fetch(`${apiBase}/api/media/purge-if-unused`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: [mediaId], propertyId: property.id }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        window.alert(`刪除失敗：${data?.error || `HTTP ${res.status}`}（已改為僅從本物業移除）`);
      } else if (data.purged?.includes(mediaId)) {
        window.alert('圖片已無其他用途，媒體庫連根拔除完成 ✓');
      } else if (data.keptInUse?.[mediaId] !== undefined) {
        window.alert('此照片仍被其他物業使用，已僅從本物業移除，媒體庫保留原檔');
      }
    } catch (err) {
      window.alert(`刪除請求失敗：${err instanceof Error ? err.message : String(err)}（已改為僅從本物業移除）`);
    }
    removeImage(imageToDelete.idx);
    setImageToDelete(null);
    // 媒體庫清單會在下次載入時自動刷新
  };

  const moveImage = (idx: number, direction: -1 | 1) => {
    const next = idx + direction;
    if (next < 0 || next >= property.images.length) return;
    const imgs = [...property.images];
    [imgs[idx], imgs[next]] = [imgs[next], imgs[idx]];
    update('images', imgs);
  };

  // 拖曳排序：把 fromIdx 的照片即時移到 toIdx（拖經即讓位，鬆手完成）
  const dragMoveTo = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
    if (fromIdx >= property.images.length || toIdx >= property.images.length) return;
    const imgs = [...property.images];
    const [moved] = imgs.splice(fromIdx, 1);
    imgs.splice(toIdx, 0, moved);
    update('images', imgs);
  };

  const handleFloorPlanSelect = (urls: string[]) => {
    if (urls[0]) update('floorPlan', urls[0]);
  };

  const handleSave = () => {
    onSave(property);
  };

  const titleText =
    mode === 'create' ? '新增物業' : mode === 'approve' ? '審核業主放盤' : '編輯物業';

  return (
    <>
      <div className="fixed inset-0 z-50 flex justify-end bg-black/50">
        <div className="flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 p-4 md:p-5">
            <h2 className="text-xl font-bold text-slate-900">{titleText}</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowPreview(true)}
                className="flex items-center gap-1.5 text-sm font-bold text-blue-800 hover:underline"
              >
                <Eye className="h-4 w-4" />
                前台預覽
              </button>
              <button onClick={onCancel} className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6">
            <div className="mb-6 rounded-lg bg-gold-50 p-4 text-sm text-gold-800">
              <p className="font-bold">提示</p>
              <p>第一張圖片會自動成為封面圖。可拖動或利用上下箭頭調整圖片顯示順序。</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <TextInput label="標題 *" value={property.title} onChange={(v) => update('title', v)} />
              <TextInput label="樓盤編號 *" value={property.code} onChange={(v) => update('code', v)} />
              <Select
                label="租售類型"
                value={property.listingType}
                onChange={(v) => update('listingType', v as Property['listingType'])}
                options={listingTypes}
              />
              <Select
                label="物業類型"
                value={property.category}
                onChange={(v) => update('category', v as Property['category'])}
                options={categories}
              />
              <Select
                label="地區"
                value={property.district}
                onChange={(v) => update('district', v as Property['district'])}
                options={districts}
              />
              <Select
                label="負責人"
                value={property.agentId}
                onChange={(v) => update('agentId', v)}
                options={agentOptions}
              />
              {isAdmin && (
                <div className="col-span-1 md:col-span-2 rounded-lg border border-gold-200 bg-gold-50 p-3">
                  <label className="mb-1 block text-sm font-semibold text-gold-900">後台權限歸屬</label>
                  {staffUsers && staffUsers.length > 0 ? (
                    <div className="text-sm text-gold-800">
                      {property.agentId && previewAgent ? (
                        <>
                          <p>
                            負責人 <span className="font-bold">{previewAgent.name}</span> 對應登入帳號{' '}
                            <span className="font-mono font-bold">{property.createdBy || '（未找到）'}</span>
                          </p>
                          <p className="mt-1 text-xs text-gold-700">
                            Staff 用此帳號登入後即可管理此物業。更改「負責人」會自動同步權限歸屬。
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-gold-700">請先選擇「負責人」，系統會自動對應到其登入帳號。</p>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gold-800">
                      {staffUsers === undefined
                        ? '正在載入帳號列表...'
                        : '未能載入 Staff 帳號列表，請重新整理頁面或檢查 API 權限。'}
                    </div>
                  )}
                </div>
              )}
              <TextInput label="分區/地點" value={property.location} onChange={(v) => update('location', v)} />
              <TextInput label="地址 *" value={property.address} onChange={(v) => update('address', v)} />
              <div className="mb-4">
                <label className="mb-1 block text-sm font-semibold text-slate-700">售價</label>
                <div className="flex overflow-hidden rounded border border-slate-300 focus-within:border-gold-600 focus-within:ring-1 focus-within:ring-gold-600">
                  <input
                    type="number"
                    value={property.price || ''}
                    onChange={(e) => handleNumber('price', e.target.value)}
                    className="flex-1 px-3 py-2 text-sm outline-none"
                  />
                  <span className="flex items-center border-l border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-500">HKD</span>
                </div>
              </div>
              <div className="mb-4">
                <label className="mb-1 block text-sm font-semibold text-slate-700">租金</label>
                <div className="flex overflow-hidden rounded border border-slate-300 focus-within:border-gold-600 focus-within:ring-1 focus-within:ring-gold-600">
                  <input
                    type="number"
                    value={property.rentPrice || ''}
                    onChange={(e) => {
                      const num = e.target.value === '' ? undefined : Number(e.target.value);
                      update('rentPrice', num as Property['rentPrice']);
                    }}
                    className="flex-1 px-3 py-2 text-sm outline-none"
                  />
                  <span className="flex items-center border-l border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-500">HKD/月</span>
                </div>
              </div>
              <Select
                label="標示"
                value={property.status}
                onChange={(v) => update('status', v as Property['status'])}
                options={[
                  { value: 'available', label: '可租售' },
                  { value: 'rented', label: '已租' },
                  { value: 'sold', label: '已售' },
                  { value: 'exclusive', label: '獨家' },
                ]}
              />
              <TextInput label="建築面積（呎）" value={property.areaGross || ''} onChange={(v) => handleNumber('areaGross', v)} type="number" />
              <TextInput label="實用面積（呎）" value={property.areaSaleable || ''} onChange={(v) => handleNumber('areaSaleable', v)} type="number" />
              <TextInput label="發布日期" value={property.publishDate || ''} onChange={(v) => update('publishDate', v)} type="date" />
              <div className="mb-4 flex items-center gap-3 pt-6">
                <input
                  id="featured"
                  type="checkbox"
                  checked={property.featured}
                  onChange={(e) => update('featured', e.target.checked)}
                  className="h-5 w-5 rounded border-slate-300 text-gold-600 focus:ring-gold-600"
                />
                <label htmlFor="featured" className="text-sm font-semibold text-slate-700">
                  設為精選物業（首頁展示）
                </label>
              </div>
            </div>

            <TextArea label="物業簡介 *" value={property.description} onChange={(v) => update('description', v)} rows={6} />

            <div className="grid gap-4 md:grid-cols-2">
              <TextInput label="標籤（以逗號分隔）" value={tagsText} onChange={handleTagsChange} placeholder="海景, 近港鐵, 甲級寫字樓" />
              <TextInput label="設施特色（以逗號分隔）" value={featuresText} onChange={handleFeaturesChange} placeholder="獨立冷氣, 24小時管理, 停車場" />
            </div>

            <TextInput
              label="Google Maps 連結"
              value={property.mapLink || ''}
              onChange={(v) => update('mapLink', v)}
              placeholder="https://www.google.com/maps/search/?api=1&query=..."
            />

            {/* Images */}
            <div className="mb-6">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <label className="block text-sm font-semibold text-slate-700">物業相簿</label>
                <div className="flex items-center gap-2">
                  {multiSelectMode && property.images.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedForDelete((prev) =>
                          prev.size === property.images.length
                            ? new Set()
                            : new Set(property.images.map((_, i) => i))
                        )
                      }
                      className="rounded border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                    >
                      {selectedForDelete.size === property.images.length ? '取消全選' : '全選'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => (multiSelectMode ? exitMultiSelect() : setMultiSelectMode(true))}
                    disabled={property.images.length === 0}
                    className={`rounded px-3 py-1.5 text-xs font-bold transition ${
                      multiSelectMode
                        ? 'bg-slate-700 text-white hover:bg-slate-800'
                        : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                    } disabled:opacity-40`}
                  >
                    {multiSelectMode ? '退出多選' : '多選刪除'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowImagePicker(true)}
                    className="flex items-center gap-1.5 rounded bg-blue-800 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-900"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    從媒體庫加入
                  </button>
                </div>
              </div>

              {multiSelectMode && selectedForDelete.size > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5">
                  <span className="text-sm font-semibold text-amber-800">已選 {selectedForDelete.size} 張</span>
                  <button
                    type="button"
                    onClick={handleBatchRemoveOnly}
                    className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    僅從本物業移除
                  </button>
                  <button
                    type="button"
                    onClick={handleBatchRemoveAndDeleteMedia}
                    disabled={batchPurging}
                    className="flex items-center gap-1.5 rounded bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {batchPurging && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    同時從媒體庫刪除
                  </button>
                  <span className="text-xs text-slate-500">仍被其他物業使用的照片會自動保留於媒體庫</span>
                </div>
              )}

              {property.images.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 py-8 text-slate-400">
                  <ImageIcon className="mb-2 h-8 w-8" />
                  <p className="text-sm">尚未加入任何圖片</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {property.images.map((img, idx) => {
                    const selected = multiSelectMode && selectedForDelete.has(idx);
                    return (
                    <div
                      key={`${img}-${idx}`}
                      onClick={multiSelectMode ? () => toggleSelectImage(idx) : undefined}
                      draggable={!multiSelectMode}
                      onDragStart={(e) => {
                        setDragIdx(idx);
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', String(idx));
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (dragIdx === null || dragIdx === idx) return;
                        dragMoveTo(dragIdx, idx);
                        setDragIdx(idx);
                      }}
                      onDragEnd={() => setDragIdx(null)}
                      onDrop={(e) => e.preventDefault()}
                      className={`group relative aspect-square overflow-hidden rounded-lg border-2 transition ${
                        selected
                          ? 'border-amber-500 ring-2 ring-amber-400'
                          : dragIdx === idx
                            ? 'border-blue-600 opacity-40'
                            : 'border-slate-200'
                      } ${multiSelectMode ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}`}
                    >
                      <img src={img} alt={`物業圖片 ${idx + 1}`} className="h-full w-full object-cover" />
                      {multiSelectMode ? (
                        <div className="absolute left-2 top-2 flex items-center gap-1.5">
                          <span
                            className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                              selected ? 'border-amber-500 bg-amber-500 text-white' : 'border-white bg-black/40 text-transparent'
                            }`}
                          >
                            <Check className="h-4 w-4" />
                          </span>
                          {idx === 0 && (
                            <span className="rounded bg-gold-600 px-2 py-0.5 text-[10px] font-bold text-white">封面</span>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="absolute left-2 top-2 flex flex-wrap gap-1.5">
                            <PropertyStatusBadge status={property.status} />
                            {idx === 0 && (
                              <span className="rounded bg-gold-600 px-2 py-0.5 text-[10px] font-bold text-white">
                                封面
                              </span>
                            )}
                          </div>
                          <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => moveImage(idx, -1)}
                                disabled={idx === 0}
                                className="rounded bg-white p-1 text-slate-700 disabled:opacity-40"
                              >
                                <ChevronUp className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveImage(idx, 1)}
                                disabled={idx === property.images.length - 1}
                                className="rounded bg-white p-1 text-slate-700 disabled:opacity-40"
                              >
                                <ChevronDown className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => setImageToDelete({ idx, url: img })}
                              className="rounded bg-red-600 p-1 text-white"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Floor plan */}
            <div className="mb-6">
              <label className="mb-2 block text-sm font-semibold text-slate-700">平面圖</label>
              {property.floorPlan ? (
                <div className="relative inline-block overflow-hidden rounded-lg border border-slate-200">
                  <img src={property.floorPlan} alt="平面圖" className="h-40 w-auto object-contain" />
                  <button
                    type="button"
                    onClick={() => update('floorPlan', undefined)}
                    className="absolute right-2 top-2 rounded bg-red-600 p-1.5 text-white"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowFloorPlanPicker(true)}
                  className="flex items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-4 py-3 text-sm font-semibold text-slate-600 hover:border-blue-800 hover:text-blue-800"
                >
                  <ImageIcon className="h-4 w-4" />
                  選擇平面圖
                </button>
              )}
            </div>

            {/* ── 社群推廣（Facebook）——production 入口；影片 tab 由 KV flag 預留 ── */}
            <div className="mt-6 rounded-lg border border-slate-200">
              <button
                type="button"
                onClick={() => setSocialOpen((v) => !v)}
                className="flex w-full items-center justify-between p-5 text-left"
              >
                <span className="flex items-center gap-2 text-base font-bold text-slate-900">
                  <Facebook className="h-5 w-5 text-blue-600" /> 社群推廣（Facebook）
                </span>
                <span className="flex items-center gap-3">
                  {property.fbPublishedAt ? (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                      已推廣 {new Date(property.fbPublishedAt).toLocaleDateString('zh-HK')}
                    </span>
                  ) : null}
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition ${socialOpen ? 'rotate-180' : ''}`} />
                </span>
              </button>

              {socialOpen && (
                <div className="space-y-4 px-5 pb-5">
                  {/* utm 追蹤資料＋成效（第一方 Analytics：短鏈點擊歸因 utm_campaign） */}
                  <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                    <div className="mb-1 font-semibold text-slate-700">utm 追蹤資料</div>
                    <div className="grid gap-1 md:grid-cols-2">
                      <div>短連結：<span className="font-mono text-blue-700">{shortLink || '生成中…'}</span></div>
                      <div className="font-mono">utm_source=facebook · utm_medium=photo · utm_campaign={property.id}</div>
                    </div>
                    {analytics && (
                      <div className="mt-2 flex flex-wrap gap-3 border-t border-slate-200 pt-2">
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-700">FB 點擊 {analytics.fbClicks}</span>
                        <span className="rounded-full bg-slate-200 px-2 py-0.5 font-semibold text-slate-700">頁面瀏覽 {analytics.views}</span>
                        <span className="text-slate-400">（更新於展開時；FB 點擊＝短連結歸因、瀏覽＝前台頁面計數）</span>
                      </div>
                    )}
                  </div>

                  {/* 模式切換：照片｜影片（影片由 staging_features flag 控制，現階段隱藏） */}
                  <div className="flex gap-2 border-b border-slate-200">
                    <button
                      type="button"
                      onClick={() => setSocialTab('photo')}
                      className={`px-3 py-2 text-sm font-semibold ${socialTab === 'photo' ? 'border-b-2 border-blue-600 text-blue-700' : 'text-slate-500'}`}
                    >
                      照片
                    </button>
                    {videoPublishEnabled ? (
                      <button
                        type="button"
                        onClick={() => setSocialTab('video')}
                        className={`px-3 py-2 text-sm font-semibold ${socialTab === 'video' ? 'border-b-2 border-blue-600 text-blue-700' : 'text-slate-500'}`}
                      >
                        影片
                      </button>
                    ) : null}
                  </div>

                  {socialTab === 'photo' && (
                    <>
                      {/* 照片選擇 */}
                      <div>
                        <label className="mb-1 block text-sm font-semibold text-slate-700">
                          發佈照片（已選 {selectedImageUrls.length}/{property.images.length} 張，上限 10）
                        </label>
                        {property.images.length === 0 ? (
                          <p className="text-sm text-slate-500">尚未上傳照片，請先在上方加入圖片。</p>
                        ) : (
                          <div className="grid grid-cols-5 gap-2 md:grid-cols-8">
                            {property.images.map((url) => {
                              const selected = selectedImageUrls.includes(url);
                              return (
                                <button
                                  key={url}
                                  type="button"
                                  onClick={() => toggleImage(url)}
                                  className={`relative overflow-hidden rounded border-2 ${selected ? 'border-blue-600' : 'border-transparent opacity-60'}`}
                                >
                                  <img src={url} alt="" className="h-16 w-full object-cover" />
                                  {selected && (
                                    <span className="absolute right-1 top-1 rounded-full bg-blue-600 p-0.5">
                                      <Check className="h-3 w-3 text-white" />
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* 貼文文案 */}
                      <div>
                        <div className="mb-1 flex items-center justify-between">
                          <label className="block text-sm font-semibold text-slate-700">
                            貼文文案（{fullMessage.length} 字，FB 上限 5000）
                          </label>
                          <button
                            type="button"
                            onClick={copyMessage}
                            className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                          >
                            <Copy className="h-3 w-3" />
                            {msgCopied ? '已複製 ✓' : '複製文案'}
                          </button>
                        </div>
                        <textarea
                          value={customMessage}
                          onChange={(e) => setCustomMessage(e.target.value)}
                          rows={6}
                          placeholder="留空＝自動生成（標題＋價格＋面積＋地址＋描述＋連結＋Hashtag）；也可完整覆寫"
                          className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs leading-relaxed focus:border-blue-600 focus:outline-none"
                        />
                        <p className="mt-1 whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs text-slate-500">{fullMessage}</p>
                      </div>

                      {/* Hashtag 分組標籤牆（點選加入；清單於「內容編輯→社群推廣標籤」維護） */}
                      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <label className="block text-sm font-semibold text-slate-700">Hashtag 標籤（點選加入貼文）</label>
                          <span className="text-xs text-slate-500">已選 {selectedTags.length} 個</span>
                        </div>
                        {selectedTags.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-1.5">
                            {selectedTags.map((t) => (
                              <button
                                type="button"
                                key={t}
                                onClick={() => toggleTag(t)}
                                className="inline-flex items-center gap-1 rounded-full border border-amber-500 bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 transition hover:bg-amber-200"
                              >
                                {t}
                                <span className="text-amber-600">✕</span>
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="space-y-1.5">
                          {hashtagGroups.map((g) => (
                            <div key={g.name} className="flex flex-wrap items-center gap-1.5">
                              <span className="w-11 shrink-0 text-xs font-semibold text-slate-500">{g.name}</span>
                              {g.tags.map((t) => {
                                const on = selectedTags.includes(t);
                                return (
                                  <button
                                    type="button"
                                    key={t}
                                    onClick={() => toggleTag(t)}
                                    className={`rounded-full border px-2.5 py-1 text-xs transition ${
                                      on
                                        ? 'border-amber-500 bg-amber-100 font-semibold text-amber-800'
                                        : 'border-slate-300 bg-white text-slate-600 hover:border-amber-400 hover:text-amber-700'
                                    }`}
                                  >
                                    {t}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                        {/* 自由輸入新標籤僅限 Admin（2026-08-28 用戶確認：Staff 只能使用現有 tag） */}
                        {isAdmin ? (
                          <div className="mt-2 flex gap-1.5">
                            <input
                              type="text"
                              value={customTagInput}
                              onChange={(e) => setCustomTagInput(e.target.value)}
                              onKeyDown={(e) => {
                                // 逗號＝完成當前標籤（支援連續輸入「A, B, C」），Enter 同效
                                if (e.key === 'Enter' || e.key === ',' || e.key === '，') {
                                  e.preventDefault();
                                  addCustomTag();
                                }
                              }}
                              placeholder="輸入新標籤，按逗號或 Enter 完成"
                              className="w-56 rounded border border-slate-300 px-2.5 py-1.5 text-xs focus:border-blue-600 focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={addCustomTag}
                              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                            >
                              加入
                            </button>
                          </div>
                        ) : (
                          <p className="mt-2 text-xs text-slate-400">如需新增標籤，請聯絡管理員於「內容編輯 → 社群推廣標籤」加入。</p>
                        )}
                      </div>

                      {publishResult && (
                        <p className={`text-sm ${publishResult.ok ? 'text-green-700' : 'text-red-600'}`}>{publishResult.message}</p>
                      )}

                      <button
                        type="button"
                        onClick={() => setConfirmPublic(true)}
                        disabled={publishing || selectedImageUrls.length === 0}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Facebook className="h-4 w-4" />}
                        {publishing ? '發佈中…' : property.fbPublishedAt ? '重新發佈到 Facebook' : '發佈到 Facebook'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 公開發佈確認（每次發佈都攔截確認） */}
          {confirmPublic && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4">
              <div className="w-full max-w-md rounded-xl bg-white p-6">
                <h3 className="mb-2 text-lg font-bold text-red-600">⚠️ 將公開發佈到粉絲頁</h3>
                <p className="mb-1 text-sm text-slate-700">
                  這則貼文（{selectedImageUrls.length} 張照片）將<strong>公開發佈</strong>到 Facebook 粉絲頁，所有訪客可見。
                </p>
                <p className="mb-4 text-xs text-slate-500">發佈後可在 Facebook 後台刪除貼文，但無法從本系統撤回。</p>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setConfirmPublic(false)} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
                    取消
                  </button>
                  <button
                    onClick={() => { setConfirmPublic(false); doPublish(); }}
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                  >
                    確認公開發佈
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-100 p-5">
            <button onClick={onCancel} className="rounded-lg px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={!property.title || !property.code || !property.address}
              className="flex items-center gap-2 rounded-lg bg-blue-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-900 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              儲存物業
            </button>
          </div>
        </div>
      </div>

      <MediaPicker
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        media={media}
        apiBase={apiBase}
        mode="multiple"
        onSelect={addImages}
        onUpload={onUploadMedia}
        onDelete={onDeleteMedia}
        usedMediaIds={usedMediaIds}
        onOpenRefresh={onRefreshMedia}
        defaultCategory={property.code || undefined}
        defaultTags={property.code ? [property.code] : undefined}
      />

      <MediaPicker
        isOpen={showFloorPlanPicker}
        onClose={() => setShowFloorPlanPicker(false)}
        media={media}
        apiBase={apiBase}
        mode="single"
        onSelect={handleFloorPlanSelect}
        onUpload={onUploadMedia}
        onDelete={onDeleteMedia}
        usedMediaIds={usedMediaIds}
        defaultCategory={property.code || undefined}
        defaultTags={property.code ? [property.code] : undefined}
      />

      {imageToDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="mb-2 text-lg font-bold text-slate-900">移除圖片</h3>
            <p className="mb-6 text-sm text-slate-600">
              您希望如何處理這張圖片？<br />
              <span className="break-all text-xs text-slate-500">{imageToDelete.url}</span>
            </p>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleRemoveImageOnly}
                className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                僅從本物業移除
              </button>
              <button
                type="button"
                onClick={handleRemoveImageAndDeleteMedia}
                className="w-full rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                同時從媒體庫刪除
              </button>
              <button
                type="button"
                onClick={() => setImageToDelete(null)}
                className="w-full rounded-lg px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-700"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4">
          <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 p-4">
              <h3 className="text-lg font-bold text-slate-900">前台預覽（未儲存）</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {property.images[0] ? (
                <div className="relative mb-5 aspect-[16/10] overflow-hidden rounded-xl bg-slate-200">
                  <img src={property.images[0]} alt={property.title} className="h-full w-full object-cover" />
                </div>
              ) : (
                <div className="mb-5 flex aspect-[16/10] items-center justify-center rounded-xl bg-slate-100 text-slate-400">
                  尚未加入圖片
                </div>
              )}

              <div className="mb-4 flex flex-wrap gap-2">
                {property.status !== 'available' ? (
                  <PropertyStatusBadge status={property.status} className="px-3 py-1.5 text-sm" pulse={false} />
                ) : (
                  <span className="bg-slate-900 px-3 py-1.5 text-sm font-bold text-white">
                    {property.listingType === 'sale' ? '放售' : property.listingType === 'rent' ? '放租' : '租售均可'}
                  </span>
                )}
                <span className="bg-gold-600 px-3 py-1.5 text-sm font-bold text-white">{categoryLabels[property.category]}</span>
              </div>

              <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-500">
                <span className="rounded bg-slate-100 px-2 py-1">{property.code}</span>
                <span>{property.location}</span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span>{districtLabels[property.district] || property.location}</span>
              </div>
              <h2 className="mb-3 text-2xl font-bold text-slate-900">{property.title}</h2>
              <div className="mb-5 flex items-center gap-2 text-sm text-slate-500">
                <MapPin className="h-4 w-4 shrink-0" />
                <span>{property.address}</span>
              </div>

              <div className="mb-6 grid gap-4 rounded-xl bg-[#f5f3ee] p-5 sm:grid-cols-2">
                <div>
                  <p className="mb-1 text-xs text-slate-500">
                    {property.listingType === 'rent' ? '每月租金' : property.listingType === 'sale_or_rent' ? '售價 / 租金' : '意向價'}
                  </p>
                  <p className="text-xl font-bold text-slate-900">
                    {property.listingType === 'sale_or_rent'
                      ? `$${property.price.toLocaleString()} / $${(property.rentPrice || 0).toLocaleString()}/月`
                      : property.listingType === 'rent'
                      ? `$${(property.rentPrice || 0).toLocaleString()}/月`
                      : `$${property.price.toLocaleString()}`}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-xs text-slate-500">面積</p>
                  <p className="text-xl font-bold text-slate-900">{property.areaGross.toLocaleString()} 呎</p>
                </div>
                {previewAgent && (
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-200 text-lg font-bold text-slate-600">
                      {previewAgent.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">{previewAgent.name}</p>
                      <p className="text-xs text-slate-500">{previewAgent.title}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-6">
                <h4 className="mb-2 font-bold text-slate-900">物業簡介</h4>
                <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700">{property.description || '（尚未填寫簡介）'}</p>
              </div>

              {property.features.length > 0 && (
                <div className="mb-6">
                  <h4 className="mb-2 font-bold text-slate-900">設施與特色</h4>
                  <div className="flex flex-wrap gap-2">
                    {property.features.map((feature) => (
                      <span key={feature} className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-sm text-slate-700">
                        <Check className="h-3.5 w-3.5 text-gold-700" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {property.tags.length > 0 && (
                <div className="mb-6">
                  <h4 className="mb-2 font-bold text-slate-900">標籤</h4>
                  <div className="flex flex-wrap gap-2">
                    {property.tags.map((tag) => (
                      <span key={tag} className="flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-600">
                        <Tag className="h-3 w-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
